import {
  Accordion,
  AccordionButton,
  AccordionItem,
  AccordionPanel,
  Box,
  Container,
  Flex,
  Grid,
  GridItem,
  StackItem,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  VStack,
} from '@chakra-ui/react';
import { nodeParser } from '@kontent-ai/delivery-node-parser';
import { createRichTextHtmlResolver } from '@kontent-ai/delivery-sdk';
import { BodyWrapper } from 'components/wrapper/bodyWrapper';
import { htmlResolver } from 'helpers/htmlResolver';
import SchemaMarkup from 'helpers/schemaMarkup';
import { useRouter } from 'next/router';
import { normalize } from 'pages/api/sitemap';
import React, { FC, useEffect, useState } from 'react';
import { withTranslations } from 'store/translations';
import { moduleMaxWidth } from 'themes/overrides';

import { LinkType } from '../../lib/enums/LinkType';
import { ContentPage, FAQ as FAQModel } from '../../models';
import { IconComp } from '../base/IconComp';
import { Link } from '../base/Link';
export const FAQ: FC<{ model: FAQModel }> = ({ model }) => {
  const router = useRouter();
  const { locale } = useRouter();
  const translations = withTranslations();
  const { common } = withTranslations();
  const [items, setItems] = useState<
    {
      ctaText?: string | undefined;
      ctaUrl?: any;
      ctaOpenInNewTab?: boolean | undefined;
      headline: string;
      bodyHtml: string;
      clientName?: string | undefined;
      yearOfCompletion?: number;
    }[]
  >();
  // const titleleft = model.elements.title?.value;
  const descleft = model.elements.description?.value;
  const titleleft = htmlResolver(model.elements.title);
  const jsonLD = model.elements.jsonLdMarkup?.value;
  const description = htmlResolver(model.elements.description);
  const expandFirstTwoItems = model.elements.firstTwoItemsToBeExpanded?.value?.[0]?.name?.toLowerCase() === 'true';
  const fetchItems = async () => {
    const items = await Promise.all(
      model.elements.items.linkedItems.map(async (item) => {
        const externalUrl = item.elements.ctaSnippetUrl.value;
        const kontentAiItem = item.elements.ctaSnippetKontentAiUrl.value[0];
        const fileFrontify = JSON.parse(item.elements.ctaSnippetFrontifyFile.value);
        const setUrl = async () => {
          if (fileFrontify) return fileFrontify.downloadUrl as string;
          if (externalUrl) return externalUrl;
          if (kontentAiItem) {
            const item = (await fetch(`/api/sitemap?codename=${kontentAiItem}&locale=${router.locale}`)
              .then((res) => res.json())
              .catch(() => null)) as ContentPage | null;
            return item ? Object.keys(normalize(item, router.locale, undefined, true, translations))?.[0] : '';
          }
        };
        return {
          headline: item.elements.headline.value,
          bodyHtml: createRichTextHtmlResolver(nodeParser).resolveRichText({
            element: item.elements.body,
            urlResolver: (linkId, linkText, link) => {
              return {
                linkHtml: `<a class="xLink" href="/${link?.urlSlug}">${linkText}</a>`,
              };
            },
          }).html,
          clientName: item.elements.clientName.value,
          yearOfCompletion: item.elements.yearOfCompletion.value,
          ...((externalUrl || fileFrontify || kontentAiItem) && {
            ctaText: item.elements.ctaSnippetCtaText.value,
            ctaUrl: await setUrl(),
            ctaOpenInNewTab: item.elements.ctaSnippetOpenIn.value[0]?.codename === 'new_tab',
          }),
        };
      }),
    );
    setItems(items);
  };

  useEffect(() => {
    fetchItems();
  }, []);
  useEffect(() => {
    fetchItems();
  }, [router.locale]);
  const data = {
    headline: model.elements.headline.value,
    items,
  };

  const isProjectsModule =
    model.elements?.moduleType.value.filter((itm) => itm.codename == 'related_projects').length > 0 ? true : false;
  return isProjectsModule ? (
    <>
      <SchemaMarkup id='faq' jsonLd={jsonLD} />
      <Box id={model.system.codename} p={{ base: 'xs', md: 's', lg: 'l' }} w={'100vw'} mx='auto' maxW={moduleMaxWidth}>
        <Container key={`${locale}`}>
          <Tabs size={{ base: 'base', md: 'md' }} w='100%'>
            <TabList justifyContent={{ base: 'space-between', sm: 'flex-start' }}>
              <Tab key={data.headline}>{data.headline}</Tab>
            </TabList>
            <TabPanels>
              <TabPanel key={`${data.headline}_panel`} pt={{ base: 'unset', lg: 'xs' }} borderColor={'transparent'}>
                {items && (
                  <Accordion allowMultiple defaultIndex={expandFirstTwoItems ? [0, 1] : []}>
                    <Grid
                      templateColumns={{
                        base: data.items?.length === 1 ? 'repeat(1, minmax(0, 1fr))' : 'repeat(1, 1fr)',
                        md: data.items?.length === 1 ? 'repeat(1, minmax(0, 0.5fr))' : 'repeat(2, 1fr)',
                        lg: 'repeat(2, 1fr)', // Always 2 columns for 3 or more
                      }}
                      justifyContent={data.items?.length === 1 ? 'center' : 'start'}
                      gridColumnGap={{ base: 's', md: 's', lg: 's' }}
                    >
                      {data.items?.map((item, index) => (
                        <GridItem key={index}>
                          <AccordionItem
                            borderTop='1px solid transparent'
                            borderBottom='1px solid'
                            borderBottomColor='mountain.20'
                            pb='xs'
                            w='100%'
                          >
                            {({ isExpanded }) => (
                              <>
                                <AccordionButton
                                  textAlign='start'
                                  alignItems='flex-start'
                                  as='button'
                                  _hover={{ cursor: 'pointer' }}
                                  justifyContent='space-between'
                                  p='0'
                                  textStyle={{ base: 'desktop.body.book.l', md: 'desktop.body.book.l' }}
                                  w='100%'
                                >
                                  {item.headline}
                                  <Flex ml='xs'>
                                    <IconComp styleName='active' iconName={isExpanded ? 'minus' : 'plus'} />
                                  </Flex>
                                </AccordionButton>

                                <AccordionPanel textStyle={{ base: 'desktop.body.book.s', md: 'desktop.body.l' }} p='0'>
                                  <Flex
                                    mt='xs'
                                    flexDir='column'
                                    gap='xs'
                                    sx={{
                                      a: {
                                        color: 'cyan.ramboll',
                                        textDecor: 'underline',
                                      },
                                      'ul, ol': {
                                        listStylePosition: 'inside',
                                      },
                                    }}
                                    dangerouslySetInnerHTML={{ __html: item.bodyHtml }}
                                  />
                                  <Flex direction={'column'} gap={'s'} marginTop={'s'}>
                                    <Flex direction={'column'}>
                                      <Box textStyle={'desktop.body.book.l'}>{common('client')}</Box>
                                      <Box textStyle={'desktop.body.l'}>{item.clientName}</Box>
                                    </Flex>
                                    <Flex direction={'column'}>
                                      <Box textStyle={'desktop.body.book.l'}>{common('completion')}</Box>
                                      <Box textStyle={'desktop.body.l'}>{item.yearOfCompletion}</Box>
                                    </Flex>
                                  </Flex>
                                  {item.ctaUrl && (
                                    <Box mt='xs'>
                                      <Link
                                        type={LinkType.SecondaryButton}
                                        href={item.ctaUrl}
                                        isExternal={item.ctaOpenInNewTab}
                                        iconName='chevronRightForLink'
                                        locale={router.locale}
                                      >
                                        {item.ctaText}
                                      </Link>
                                    </Box>
                                  )}
                                </AccordionPanel>
                              </>
                            )}
                          </AccordionItem>
                        </GridItem>
                      ))}
                    </Grid>
                  </Accordion>
                )}
              </TabPanel>
            </TabPanels>
          </Tabs>
        </Container>
      </Box>
    </>
  ) : (
    <>
      <SchemaMarkup id='faq' jsonLd={jsonLD} />
      {titleleft != null ? (
        <Box maxW='1440px' pt='18px'>
          <Box
            w='100%'
            // pl= '80px'
            // pr='80px'
            //pt='117px'
            //  pt={{ base: '80px', md: '117px' }}
            gap={'10px'}
            display={'flex'}
            //  bgColor={textbg == 'full' ? textbgColor : 'white'}
            flexDirection={{ base: 'column', md: 'row' }}
            //alignItems="flex-start" justifyItems="space-between"
          >
            <Box
              w={{ base: '100%', md: '45%' }}
              //bg={textbgColor}
              //   pl='80px'
              pl={{ base: '20px', md: '80px' }}
              pr={{ base: '20px', md: '80px' }}
              pt={{ base: '80px', md: '122px' }}
              pb={{ base: '0px', md: '122px' }}
              // pr='80px'
            >
              <Text
                w={{ base: '100%', md: '300%' }}
                color={'#273943'}
                /* Desktop/H3 */
                textStyle={'desktop.h3'}
                pb='51px'
              >
                {titleleft?.map((text, index) => (
                  <BodyWrapper key={index} sx={index ? { pt: 'xxs' } : {}} tag={text.tag} body={text.body} />
                ))}
              </Text>
              {/* <Box>{<BodyWrapper tag={descleft} body={descleft} />}</Box> */}
              <Box textStyle={{ base: 'mobile.body.s', lg: 'desktop.body.l' }}>
                {description?.map((text, index) => (
                  <BodyWrapper key={index} sx={index ? { pt: 'xxs' } : {}} tag={text.tag} body={text.body} />
                ))}
              </Box>
            </Box>
            <Box
              w={{ base: '100%', md: '55%' }}
              //  bgColor={textbg == 'full' ? textbgColor : 'white'}
              pt={{ base: '80px', md: '162px' }}
              pl={{ base: '20px', md: '20px' }}
              pr={{ base: '20px', md: '20px' }}
            >
              <VStack my={{ base: 's', md: 'l' }}>
                {/* <StackItem as='h2' textStyle={{ base: 'mobile.h3', md: 'desktop.h3' }} color='cyan.ramboll' mb='m'>
          {data.headline}
        </StackItem> */}
                <Accordion allowToggle w='100%' flexDirection={'column'}>
                  {data.items?.map((item, index) => (
                    <AccordionItem
                      key={index}
                      borderTop={'1px solid transparent'}
                      borderBottom={'1px solid'}
                      borderBottomColor={'mountain.20'}
                      pb='xs'
                      pt={index && 's'}
                      w='100%'
                    >
                      {({ isExpanded }) => {
                        return (
                          <>
                            <AccordionButton
                              textAlign='start'
                              as={'button'}
                              _hover={{ cursor: 'pointer' }}
                              justifyContent='space-between'
                              p='0'
                              textStyle={{ base: 'desktop.body.book.s', md: 'desktop.body.book.l' }}
                              w='100%'
                            >
                              {item.headline}
                              <Flex ml='xs'>
                                <IconComp styleName='active' iconName={isExpanded ? 'minus' : 'plus'} />
                              </Flex>
                            </AccordionButton>
                            <AccordionPanel textStyle={{ base: 'desktop.body.book.s', md: 'desktop.body.l' }} p='0'>
                              <Flex
                                mt='xs'
                                flexDir='column'
                                gap='xs'
                                sx={{
                                  a: {
                                    color: 'cyan.ramboll',
                                    textDecor: 'underline',
                                  },
                                  'ul, ol': {
                                    listStylePosition: 'inside',
                                  },
                                }}
                                dangerouslySetInnerHTML={{ __html: item.bodyHtml }}
                              />
                              {item.ctaUrl && (
                                <Box mt='xs'>
                                  <Link
                                    type={LinkType.SecondaryButton}
                                    href={item.ctaUrl}
                                    isExternal={item.ctaOpenInNewTab}
                                    iconName='chevronRightForLink'
                                    locale={router.locale}
                                  >
                                    {item.ctaText}
                                  </Link>
                                </Box>
                              )}
                            </AccordionPanel>
                          </>
                        );
                      }}
                    </AccordionItem>
                  ))}
                </Accordion>
              </VStack>
            </Box>
          </Box>
        </Box>
      ) : (
        <>
          <SchemaMarkup id='faq' jsonLd={jsonLD} />
          <Box w={{ base: '100vw', '1md': '780px' }} px={{ base: 'xs', '1md': '0px' }} id={model.system.codename}>
            <VStack my={{ base: 's', md: 'l' }}>
              <StackItem as='h2' textStyle={{ base: 'mobile.h3', md: 'desktop.h3' }} color='cyan.ramboll' mb='m'>
                {data.headline}
              </StackItem>
              <Accordion allowToggle w='100%' flexDirection={'column'}>
                {data.items?.map((item, index) => (
                  <AccordionItem
                    key={index}
                    borderTop={'1px solid transparent'}
                    borderBottom={'1px solid'}
                    borderBottomColor={'mountain.20'}
                    pb='xs'
                    pt={index && 's'}
                    w='100%'
                  >
                    {({ isExpanded }) => {
                      return (
                        <>
                          <AccordionButton
                            textAlign='start'
                            as={'button'}
                            _hover={{ cursor: 'pointer' }}
                            justifyContent='space-between'
                            p='0'
                            textStyle={{ base: 'desktop.body.book.s', md: 'desktop.body.book.l' }}
                            w='100%'
                          >
                            {item.headline}
                            <Flex ml='xs'>
                              <IconComp styleName='active' iconName={isExpanded ? 'minus' : 'plus'} />
                            </Flex>
                          </AccordionButton>
                          <AccordionPanel textStyle={{ base: 'desktop.body.book.s', md: 'desktop.body.l' }} p='0'>
                            <Flex
                              mt='xs'
                              flexDir='column'
                              gap='xs'
                              sx={{
                                a: {
                                  color: 'cyan.ramboll',
                                  textDecor: 'underline',
                                },
                                'ul, ol': {
                                  listStylePosition: 'inside',
                                },
                              }}
                              dangerouslySetInnerHTML={{ __html: item.bodyHtml }}
                            />
                            {item.ctaUrl && (
                              <Box mt='xs'>
                                <Link
                                  type={LinkType.SecondaryButton}
                                  href={item.ctaUrl}
                                  isExternal={item.ctaOpenInNewTab}
                                  iconName='chevronRightForLink'
                                  locale={router.locale}
                                >
                                  {item.ctaText}
                                </Link>
                              </Box>
                            )}
                          </AccordionPanel>
                        </>
                      );
                    }}
                  </AccordionItem>
                ))}
              </Accordion>
            </VStack>
          </Box>
        </>
      )}
    </>
  );
};
